import React, { forwardRef } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { ICON_MAP, type IconName, type IconWeight } from 'shared/constants/iconMap';
import { cn } from 'shared/utils/cn';

type BaseButtonProps = {
  variant?: 'primary' | 'secondary' | 'inverted' | 'gold' | 'ghost' | 'link' | 'underline';
  size?: 'sm' | 'md' | 'lg';
  icon?: IconName;
  iconWeight?: IconWeight;
  iconPosition?: 'left' | 'right';
};

export type ButtonProps =
  | (BaseButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement> & { to?: undefined })
  | (BaseButtonProps & LinkProps);

const sizeClasses: Record<NonNullable<BaseButtonProps['size']>, string> = {
  sm: 'px-4 py-1.5 text-sm',
  md: 'px-6 py-2 text-base',
  lg: 'px-8 py-3 text-lg',
};

const iconSizes: Record<NonNullable<BaseButtonProps['size']>, number> = {
  sm: 16,
  md: 18,
  lg: 20,
};

const variantClasses: Record<NonNullable<BaseButtonProps['variant']>, string> = {
  primary: 'bg-btn-primary text-btn-primary-text',
  secondary: 'bg-btn-secondary text-btn-secondary-text',
  inverted: 'bg-btn-inverted text-btn-inverted-text',
  gold: 'bg-tertiary text-on-tertiary font-bold',
  ghost: 'border border-outline-variant/20 text-on-surface hover:bg-primary-container',
  link: 'text-tertiary font-bold',
  underline: 'text-tertiary font-medium border-b border-tertiary/30 pb-2 hover:border-tertiary rounded-none px-0',
};

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', icon, iconWeight, iconPosition = 'left', className, children, ...props }, ref) => {
    const IconComponent = icon ? ICON_MAP[icon] : null;
    const renderedIcon = IconComponent ? <IconComponent size={iconSizes[size]} weight={iconWeight} /> : null;

    const classes = cn(
      'rounded-full font-sans font-medium transition-transform duration-200',
      'hover:scale-[1.02] active:scale-[0.98]',
      'focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:outline-none',
      renderedIcon && 'inline-flex items-center gap-2',
      sizeClasses[size],
      variantClasses[variant],
      'disabled' in props && props.disabled && 'opacity-[0.38] pointer-events-none',
      className,
    );

    const content = (
      <>
        {iconPosition === 'left' && renderedIcon}
        {children}
        {iconPosition === 'right' && renderedIcon}
      </>
    );

    if ('to' in props && props.to !== undefined) {
      const { to, ...linkProps } = props as LinkProps;
      return (
        <Link ref={ref as React.Ref<HTMLAnchorElement>} to={to} className={classes} {...linkProps}>
          {content}
        </Link>
      );
    }

    const { disabled, ...buttonProps } = props as React.ButtonHTMLAttributes<HTMLButtonElement>;
    return (
      <button ref={ref as React.Ref<HTMLButtonElement>} disabled={disabled} className={classes} {...buttonProps}>
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';
